import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { navItems, navSpacing, socialLinks } from '../constants';
import NavLink from './NavLink';
import SocialIcon from './SocialIcon';
import { LinkedInIcon } from './icons';
import EmailButton from './shared/EmailButton';

function Navigation() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Close mobile menu when clicking outside or on a link
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileMenuOpen && !event.target.closest('nav')) {
        setIsMobileMenuOpen(false);
      }
    };

    const handleLinkClick = () => {
      setIsMobileMenuOpen(false);
    };

    if (isMobileMenuOpen) {
      document.addEventListener('click', handleClickOutside);
      document.addEventListener('scroll', handleLinkClick);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('scroll', handleLinkClick);
    };
  }, [isMobileMenuOpen]);

  return (
    <>
      {/* Desktop Navigation */}
      <nav className="fixed top-6 left-1/2 transform -translate-x-1/2 z-50 hidden md:block">
        <div className="bg-nav-bg rounded-[100px] px-4 md:px-8 py-3 shadow-md">
          <ul className="flex list-none gap-4 md:gap-8 items-center">
            <li className="flex items-center ml-0 md:ml-1 justify-center">
              <Link
                to="/"
                onClick={() => window.scrollTo(0, 0)}
                className="text-primary font-normal hover:underline active:underline transition-all text-lg md:text-2xl font-playfair font-medium whitespace-nowrap"
              >
                stephanie zhang
              </Link>
              {navItems.map((item, index) => (
                <span key={item.href}>
                  <NavLink
                    href={item.href}
                    label={item.label}
                    isExternal={item.isExternal}
                    spacing={navSpacing[index] || ''}
                  />
                  {item.hasSeparator && (
                    <span className="ml-5 h-4 w-px bg-border"></span>
                  )}
                </span>
              ))}
              <span className="ml-4 mr-4 h-4 w-px bg-[#999999]"></span>
              <EmailButton className="text-primary hover:opacity-70 transition-opacity flex items-center justify-center ml-5" />
              <SocialIcon 
                href={socialLinks.linkedin} 
                icon={<LinkedInIcon />} 
                label="LinkedIn"
              />
            </li>
          </ul>
        </div>
      </nav>

      {/* Mobile Navigation */}
      <nav className="fixed top-6 left-0 right-0 z-50 md:hidden px-4">
        <div 
          className="bg-nav-bg pl-6 pr-4 shadow-md transition-all duration-300 ease-in-out overflow-hidden py-3"
          style={{
            borderRadius: isMobileMenuOpen ? '2.25rem' : '3rem',
            transition: 'border-radius 300ms ease-in-out'
          }}
        >
          {/* Header Row - Always Visible */}
          <div className="flex items-center w-full">
            <Link
              to="/"
              className="text-primary font-normal hover:underline active:underline transition-all text-2xl font-playfair font-medium whitespace-nowrap"
              onClick={() => {
                setIsMobileMenuOpen(false);
                window.scrollTo(0, 0);
              }}
            >
              stephanie zhang
            </Link>
            <div className="flex-1"></div>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-primary p-2 hover:opacity-70 transition-opacity relative"
              aria-label="Toggle menu"
            >
              <span 
                className="material-symbols-outlined text-2xl transition-all duration-300 ease-in-out"
                style={{
                  transform: isMobileMenuOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                  opacity: 1
                }}
              >
                {isMobileMenuOpen ? 'close' : 'menu'}
              </span>
            </button>
          </div>

          {/* Menu Items - Expand/Collapse */}
          <div 
            className="overflow-hidden"
            style={{
              maxHeight: isMobileMenuOpen ? '500px' : '0',
              opacity: isMobileMenuOpen ? 1 : 0,
              transition: 'max-height 300ms ease-out, opacity 300ms ease-out',
              transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            <div className="flex flex-col pb-2 pt-4">
              {navItems.map((item, index) => (
                <div 
                  key={item.href} 
                  className="pl-0 pr-0 flex justify-center"
                  style={{ marginBottom: index < navItems.length - 1 ? '1.5rem' : '0' }}
                >
                  <NavLink
                    href={item.href}
                    label={item.label}
                    isExternal={item.isExternal}
                    spacing=""
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="text-xl font-medium"
                  />
                </div>
              ))}
              <div className="h-px bg-[#E5E5E5] my-4"></div>
              <div className="pl-0 pr-0 py-2 flex items-center justify-center gap-6">
                <EmailButton 
                  className="text-primary hover:opacity-70 transition-opacity flex items-center justify-center" 
                  showIcon={true}
                />
                <SocialIcon 
                  href={socialLinks.linkedin} 
                  icon={<LinkedInIcon />} 
                  label="LinkedIn"
                  className="ml-0"
                />
              </div>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
}

export default Navigation;
